import {
  type CloneMetricsResponse,
  useGetCloneMetricsQuery,
  useGetRevisionsQuery,
} from '@carboncopyco/data';
import type { Revision } from '@carboncopyco/types';
import {
  Separator,
  TextBase,
  TextLargeParagraph,
  TextSectionHeader,
} from '@carboncopyco/ui';
import { formatSeconds } from '@carboncopyco/utils';
import { useEffect, useRef } from 'react';
import { TitleCard } from '../../InfoPageCards/TitleCard';
import { useClonePageProps } from '../ClonePageContext';

export const CloneOverviewCard = () => {
  const { organizationId, cloneId, workspaceId } = useClonePageProps();
  const {
    data: metrics,
    error,
    refetch,
  } = useGetCloneMetricsQuery({
    organizationId: organizationId,
    recordingId: cloneId as string,
    workspacesIds: [workspaceId],
  });
  const duration = metrics?.durationInSeconds;
  const clicks = metrics?.clicks;
  const pages = metrics?.uniqueFullUrls;

  const { data: revisionData } = useGetRevisionsQuery({
    organizationId,
    sourceType: 'record',
    id: String(cloneId),
    reverseOrder: true,
  });
  const revisions = revisionData?.items;
  const latestRevision = revisions?.[0];
  const prevLatestRevisionRef = useRef<Revision>(latestRevision);

  useEffect(() => {
    if (latestRevision !== prevLatestRevisionRef.current) {
      refetch();
    }
    prevLatestRevisionRef.current = latestRevision;
  }, [latestRevision, refetch]);

  return (
    <TitleCard
      header={
        <div className="flex justify-between items-center">
          <TextLargeParagraph
            color="text-ds-natural-700"
            label="Clone Overview"
          />
        </div>
      }
      radius
      padding="xl"
    >
      {error ? (
        <div className="my-3 py-9 flex flex-col items-center gap-2">
          <TextBase
            color="text-ds-natural-700"
            label="Something went wrong"
          />
        </div>
      ) : (
        <div>
          <div className="flex py-8 my-0.5">
            <div className="flex-1 text-center mr-7 ml-1">
              <TextSectionHeader
                label={
                  duration
                    ? formatSeconds(duration, {
                        format: 'H[h] m[m]',
                        formatForLessThanHour: 'm[m]',
                        formatForLessThanMinute: 's[s]',
                      })
                    : '-'
                }
                color="text-ds-natural-600"
              />
              <TextBase
                label="Cloning time"
                ellipsis
                color="text-ds-natural-600"
              />
            </div>
            <div className="relative">
              <Separator horizontal={false} />
            </div>
            <div className="flex-1 text-center mx-7">
              <TextSectionHeader
                label={clicks || '0'}
                color="text-ds-natural-600"
              />
              <TextBase
                label="# Clicks"
                ellipsis
                color="text-ds-natural-600"
              />
            </div>
            <div className="relative">
              <Separator horizontal={false} />
            </div>
            <div className="flex-1 text-center ml-7 mr-1">
              <TextSectionHeader
                label={pages || '0'}
                color="text-ds-natural-600"
              />
              <TextBase
                label="# Pages"
                ellipsis
                color="text-ds-natural-600"
              />
            </div>
          </div>
        </div>
      )}
    </TitleCard>
  );
};

export const hasMetricsData = (metrics: CloneMetricsResponse) => {
  return (
    metrics?.durationInSeconds || metrics?.clicks || metrics?.uniqueFullUrls
  );
};
